import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface } from '@hutsix/ngxh6';
import { ApplyTarget, INFO_PAGE_APPLY } from './info-page-apply';

interface PublicPage {
    title: string;
    slug: string;
    body: string | null;
    files?: { name: string; url: string }[];
    faqItems?: { question: string; answer: string | null }[];
    /** The role key on a system info page, driving its "Apply" button(s). Null on a normal page. */
    systemKey?: string | null;
}

/** A recreation site, listed on the recreation-area / boat-access info pages. */
interface RecreationSite {
    name: string;
    boatAccess: boolean;
    dayUseOnly: boolean;
    maxNights: number | null;
    capacity: number | null;
    /** One booking takes the whole site - shown as a selling point instead of a capacity. */
    exclusiveUse: boolean;
    /** May be empty - the list is built to look right with or without images. */
    photos: { url: string; caption: string | null }[];
}

/** A FAQ item plus its anchor id and open state (for deep-linking / accordion). */
interface FaqEntry {
    question: string;
    answer: string | null;
    /** DOM id and URL fragment, e.g. "faq-do-i-need-a-permit". */
    anchor: string;
    open: boolean;
}

/**
 * A public information page (no login), rendered by slug from the mini-CMS - the
 * applicant-facing side of the Config → Pages manager. Wired as the `:slug` route, which
 * sits after every real route and before the catch-all, so a real screen always wins and
 * only genuine page slugs (or unknown addresses) reach here.
 *
 * Fetches /api/public/page/:slug as a guest (skipAuth + bypassOfflineQueue, the same as
 * the other public pages), and shows a plain "not found" for a slug that isn't a
 * published page. The body is staff-authored rich text, rendered through Angular's
 * sanitiser.
 *
 * FAQ deep-linking: the FAQ block is `#faq`, and each item gets a stable slug anchor
 * derived from its question (e.g. `#faq-do-i-need-a-permit`) - a slug rather than an
 * index, so a shared link survives items being reordered or inserted. Arriving on a
 * matching fragment opens that item and scrolls to it.
 */
@Component({
    selector: 'app-page',
    templateUrl: './page.component.html',
    styleUrls: ['./page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PageComponent implements OnInit {
    public page?: PublicPage | null;
    public faqItems: FaqEntry[] = [];
    /** Recreation sites listed on the recreation-area / boat-access info pages (dynamic). */
    public recreationSites: RecreationSite[] = [];
    public loading = true;
    public notFound = false;

    /** The current URL fragment (#...), applied once the page's FAQ items are in. */
    private fragment: string | null = null;

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        private route: ActivatedRoute,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        // paramMap, not snapshot: moving between two pages (e.g. a menu link from one page
        // to another) reuses this component, so we re-fetch when the slug changes.
        this.route.paramMap.subscribe(params => {
            const slug = params.get('slug');
            this.loading = true;
            this.notFound = false;
            this.page = null;
            this.faqItems = [];
            this.recreationSites = [];
            this.cdRef.markForCheck();

            this.api
                .get({ url: `/api/public/page/${slug}`, skipAuth: true, bypassOfflineQueue: true, useCache: false, displayErrors: false })
                .subscribe(
                    (res: ApiResponse<PublicPage>) => {
                        this.loading = false;
                        this.page = res.data;
                        // Rich text pasted from Word arrives with a non-breaking space
                        // between every word, which turns each paragraph into one
                        // unbreakable line that can't wrap and overflows the page. Collapse
                        // those back to ordinary spaces so the text wraps at word boundaries.
                        if (this.page?.body) {
                            this.page.body = this.normalizeSpaces(this.page.body);
                        }
                        this.faqItems = this.buildFaqItems(res.data?.faqItems);
                        // The two recreation info pages list their sites dynamically: the
                        // boat-access page the boat sites, the other the road ones.
                        if ('recreation_area' === this.page?.systemKey || 'boat_access' === this.page?.systemKey) {
                            this.loadRecreationSites('boat_access' === this.page.systemKey ? 'boat' : 'road');
                        }
                        // The guest GET resolves outside Angular's change detection, so nudge
                        // it - without this the view stays on "Loading…" though the data is in.
                        this.cdRef.markForCheck();
                        // Now that the items (and their anchors) exist, honour any deep link.
                        this.applyFragment();
                    },
                    (_err: ApiErrorResponse<any>) => {
                        this.loading = false;
                        this.notFound = true;
                        this.cdRef.markForCheck();
                    },
                );
        });

        // A fragment can arrive on first load (a deep link) or change while the page is
        // open (a routerLink fragment). Either way, re-apply once we have it.
        this.route.fragment.subscribe(fragment => {
            this.fragment = fragment;
            this.applyFragment();
        });
    }

    /**
     * The "Apply" button(s) for this page, straight to the application form. Non-empty only
     * on a system info page (by its systemKey) - a normal content page has none.
     */
    public get applyTargets(): ApplyTarget[] {
        return (this.page?.systemKey && INFO_PAGE_APPLY[this.page.systemKey]) || [];
    }

    /**
     * A flow-aware "back" link (not browser history): the step this info page was reached
     * from. The two recreation pages came from the recreation splitter; every other info
     * page came straight from the Apply page. Null on a normal content page.
     */
    public get backLink(): { label: string; route: string } | null {
        const key = this.page?.systemKey;
        if (!key) {
            return null;
        }
        if ('recreation_area' === key || 'boat_access' === key) {
            return { label: 'Back to recreation options', route: '/apply/recreation-choose' };
        }
        // General content pages (the FAQ, reached from the homepage or the menu - not a step
        // in the apply flow) carry no flow back-link. Only the permit/BL info pages, which
        // are reached from the Apply screen, return there.
        if ('faq' === key) {
            return null;
        }
        return { label: 'Back to permit types', route: '/apply' };
    }

    /** The placeholder icon for a site with no photo - a boat for the boat-access page. */
    public get sitePlaceholderIcon(): string {
        return 'boat_access' === this.page?.systemKey ? 'fa-anchor' : 'fa-campground';
    }

    /** A one-line summary of a site's camping rules + capacity, for its card. */
    public siteFacts(site: RecreationSite): string {
        const bits: string[] = [];
        bits.push(site.dayUseOnly ? 'Day-use only' : site.maxNights ? `Up to ${site.maxNights} night${1 === site.maxNights ? '' : 's'}` : 'Camping');
        // Vehicle capacity is a shared-site idea; an exclusive site takes one party whatever
        // the number, so the count would mislead - its "whole site" badge says it instead.
        if (!site.exclusiveUse && site.capacity) {
            bits.push(`${site.capacity} vehicle${1 === site.capacity ? '' : 's'}`);
        }
        return bits.join(' · ');
    }

    /** Fetch the sites for a recreation info page - boat-access or road. Non-fatal on error. */
    private loadRecreationSites(access: 'boat' | 'road'): void {
        this.api
            .get({ url: `/api/public/recreation-areas?access=${access}`, skipAuth: true, bypassOfflineQueue: true, useCache: false, displayErrors: false })
            .subscribe(
                (res: ApiResponse<RecreationSite[]>) => {
                    this.recreationSites = res.data ?? [];
                    this.cdRef.markForCheck();
                },
                () => {
                    // A failed site list just leaves the section empty; the page still works.
                },
            );
    }

    /** Keep the details' open state in sync when the user toggles it by hand. */
    public onToggle(item: FaqEntry, event: Event): void {
        item.open = (event.target as HTMLDetailsElement).open;
    }

    private buildFaqItems(raw?: { question: string; answer: string | null }[]): FaqEntry[] {
        const seen = new Map<string, number>();

        return (raw ?? []).map(item => {
            const base = 'faq-' + (this.slugify(item.question) || 'item');
            // De-dupe: two questions that slugify the same get -2, -3… so anchors stay unique.
            const count = (seen.get(base) ?? 0) + 1;
            seen.set(base, count);

            return {
                question: item.question,
                answer: this.normalizeSpaces(item.answer),
                anchor: count === 1 ? base : `${base}-${count}`,
                open: false,
            };
        });
    }

    /**
     * Collapse non-breaking spaces (the literal &nbsp; entity and the U+00A0 character)
     * to ordinary spaces, so Word-pasted rich text wraps instead of running off the page.
     */
    private normalizeSpaces(html: string | null): string | null {
        return html?.replace(/&nbsp;| /g, ' ') ?? html;
    }

    private slugify(value: string): string {
        return (value || '')
            .toLowerCase()
            .normalize('NFKD')
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/[\s_]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 60)
            .replace(/-$/g, '');
    }

    /**
     * Open + scroll to the item the fragment names (or scroll to the FAQ block for #faq).
     * Safe to call before items load - it simply finds nothing until they're in.
     */
    private applyFragment(): void {
        const fragment = this.fragment;
        if (!fragment) {
            return;
        }

        const target = this.faqItems.find(item => item.anchor === fragment);
        if (target) {
            target.open = true;
            this.cdRef.markForCheck();
        } else if (fragment !== 'faq') {
            return; // not an anchor we own
        }

        // Scroll after the open state has rendered.
        setTimeout(() => document.getElementById(fragment)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
}
